import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { ICommercialFeedDataGridRow } from "../types";
import { calculateTotalPrice } from "../utils/feedUtils";

interface Props {
  open: boolean;
  targetIds: number[];
  targetRows: ICommercialFeedDataGridRow[];
  unitPrice: number | null;
  priceError: string | null;
  onRetryPrice: () => void;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
}

export const MaskUnlockConfirmDialog: React.FC<Props> = ({
  open,
  targetIds,
  targetRows,
  unitPrice,
  priceError,
  onRetryPrice,
  onConfirm,
  onClose,
  isLoading,
  error,
}) => {
  const count = targetIds.length;
  const totalPrice = calculateTotalPrice(count, unitPrice);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LockIcon fontSize="small" />
        {count}件の法人情報を{count > 1 ? "一括閲覧" : "閲覧"}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {priceError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetryPrice}>
                再試行
              </Button>
            }
          >
            {priceError}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          会社法人等番号・法人番号と法人名の閲覧には1件あたり{" "}
          {unitPrice !== null ? (
            <strong>¥{unitPrice.toLocaleString("ja-JP")}</strong>
          ) : (
            "取得中..."
          )}
          かかります。閲覧後はマスクが解除され、エクスポート対象に含まれます。
        </Typography>

        <Box
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {targetRows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1,
                borderBottom:
                  index < targetRows.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2">
                {/* 会社法人等番号が空の行でも識別子が空欄にならないよう法人番号で補う */}
                {row.requesterCommercialRegistrationNumber ||
                  row.requesterCorporateNumber}
                {row.legalAffairsBureauReceptionNumber
                  ? ` 受付 ${row.legalAffairsBureauReceptionNumber}`
                  : ""}
                {row.legalAffairsBureauRequestDate
                  ? ` / ${row.legalAffairsBureauRequestDate}`
                  : ""}
              </Typography>
              <Typography variant="body2" sx={{ flexShrink: 0, ml: 2 }}>
                {unitPrice !== null
                  ? `¥${unitPrice.toLocaleString("ja-JP")}`
                  : "-"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            pt: 1.5,
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              単価
            </Typography>
            <Typography variant="body2">
              {unitPrice !== null
                ? `¥${unitPrice.toLocaleString("ja-JP")} × ${count}件`
                : priceError
                ? "取得失敗"
                : "取得中..."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1" fontWeight="bold">
              合計
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {totalPrice !== null
                ? `¥${totalPrice.toLocaleString("ja-JP")}`
                : priceError
                ? "-"
                : "取得中..."}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            表示金額は目安です。実際の請求額はご利用時点の価格・購入状況に基づきサーバー側で確定します。
          </Typography>
        </Box>

        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          icon={<WarningAmberIcon fontSize="inherit" />}
        >
          一度閲覧した法人情報は、再課金なく表示が可能です。利用明細はマイページに記録されます。
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          キャンセル
        </Button>
        <Button
          onClick={() => {
            void onConfirm();
          }}
          disabled={isLoading || unitPrice === null}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          閲覧する
        </Button>
      </DialogActions>
    </Dialog>
  );
};
